import type { ApiBaseUrlValue, DebugConfig } from './storage'

declare global {
  interface Window {
    RTR_API_URL_OVERWRITTEN: ApiBaseUrlValue
    RTR_API_BASIC_AUTH_USER: string
    RTR_API_BASIC_AUTH_PASS: string
  }
}

export interface Account {
  id: string
  name: string
  widgetAccessToken: string
}

export interface Item {
  id: string
  name: string
  reference: string
}

// Get config endpoint from environment variable or use default
const getConfigEndpoint = (): string => {
  // In Stencil, we can't directly access process.env at runtime
  // The endpoint path should be consistent across environments
  return '/__dev/config'
}

export const api = {
  async fetchDebugConfig(baseUrl: string): Promise<DebugConfig> {
    try {
      const configEndpoint = getConfigEndpoint()
      const response = await fetch(`${baseUrl}${configEndpoint}`, {
        method: 'GET',
        mode: 'cors',
        cache: 'no-cache',
        credentials: 'same-origin',
        headers: {
          'Content-Type': 'application/json'
        },
        redirect: 'follow',
        referrerPolicy: 'no-referrer'
      })

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`)
      }

      const result = await response.json()
      return result
    } catch (error) {
      const errorMessage = `Could not fetch debug config from ${baseUrl}${getConfigEndpoint()}`
      console.error(errorMessage, error)
      throw new Error(errorMessage)
    }
  },

  async fetchAccounts(): Promise<Account[]> {
    try {
      const authString = `${window.RTR_API_BASIC_AUTH_USER}:${window.RTR_API_BASIC_AUTH_PASS}`
      const response = await fetch(`${window.RTR_API_URL_OVERWRITTEN}/__dev/accounts`, {
        method: 'GET',
        mode: 'cors',
        cache: 'no-cache',
        credentials: 'same-origin',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Basic ${btoa(authString)}`
        },
        redirect: 'follow',
        referrerPolicy: 'no-referrer'
      })

      const result = await response.json()
      return result || []
    } catch (error) {
      const errorMessage = `Could not fetch accounts from ${window.RTR_API_URL_OVERWRITTEN}`

      if (window.RTR_API_URL_OVERWRITTEN === 'http://127.0.0.1:8000') {
        console.warn('Make sure you have a local API running at http://127.0.0.1:8000')
      }

      console.error(errorMessage, error)
      throw new Error(errorMessage)
    }
  },

  async fetchItems(accountId: string): Promise<Item[]> {
    try {
      const authString = `${window.RTR_API_BASIC_AUTH_USER}:${window.RTR_API_BASIC_AUTH_PASS}`
      const response = await fetch(`${window.RTR_API_URL_OVERWRITTEN}/__dev/accounts/${accountId}/items`, {
        method: 'GET',
        mode: 'cors',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Basic ${btoa(authString)}`
        }
      })

      const result = await response.json()
      return result || []
    } catch (error) {
      const errorMessage = `Could not fetch items from ${window.RTR_API_URL_OVERWRITTEN}`
      console.error(errorMessage, error)
      throw new Error(errorMessage)
    }
  }
}
